import React, { FC, useState } from 'react';
import Paragraph from '~ComponentsImproved/Typography/Paragraph';
import { useButton, useI18n } from '~Hooks';
import { HeaderBox } from './styles';
import { useDeviceHandler } from '~Components/DeviceProvider';
import Icon from '~ComponentsImproved/Icon';
import Heading from '~ComponentsImproved/Typography/Heading';
import DevicesIcon from '~Assets/icons/devices.svg';
import Create from './Components/Create';
import Claim from './Components/Claim';
import { BlueButton, Box, Button } from '~Styles';

enum VIEW {
  SELECT,
  CREATE,
  CLAIM,
}

interface Props {
  isHandy?: boolean;
  /** If we're in settings and just want to change the device */
  isChangeOfDevice?: boolean;
  onComplete: () => void;
}

/**
 * This component is responsible for claiming (and creating) a device.
 */
const ClaimDevice: FC<Props> = ({ isHandy, isChangeOfDevice, onComplete }) => {
  const { i18n } = useI18n();

  const deviceHandler = useDeviceHandler();

  const hasNoDevices = deviceHandler.allDevices.length === 0;

  const [view, setView] = useState<VIEW>(hasNoDevices ? VIEW.CREATE : VIEW.SELECT);

  const renderHeadingText = () => {
    switch (view) {
      case VIEW.CREATE:
        return i18n('ClaimDevice.RegisterDevice.Create');
      case VIEW.CLAIM:
        return i18n('ClaimDevice.RegisterDevice.Claim');
      default:
        return isChangeOfDevice
          ? i18n('ClaimDevice.ChangeDevice')
          : i18n('ClaimDevice.RegisterDevice');
    }
  };

  const renderSubtitleText = () => {
    switch (view) {
      case VIEW.CREATE:
        return i18n('ClaimDevice.RegisterDevice.CreateText');
      case VIEW.CLAIM:
        return i18n('ClaimDevice.RegisterDevice.ClaimText');
      default:
        return i18n('ClaimDevice.Select.CreateOrSelect');
    }
  };

  const createNewButton = useButton({
    onClick: () => {
      setView(VIEW.CREATE);
    },
  });

  const selectExistingButton = useButton({
    onClick: () => {
      setView(VIEW.CLAIM);
    },
  });

  return (
    <>
      <HeaderBox
        display="flex"
        flexDirection="column"
        alignItems="center"
        marginTop={isHandy ? 'small' : undefined}
        gap="xs"
        justifyContent="center">
        <Icon
          height={isHandy ? '48px' : '64px'}
          width={isHandy ? '48px' : '64px'}
          src={DevicesIcon}
          color="var(--colorBlueGray200)"
        />
        <Heading level={isHandy ? 3 : 1} color="var(--colorWhite)">
          {renderHeadingText()}
        </Heading>
        <Paragraph level={isHandy ? 4 : 2} bottom={0} color="var(--colorBlueGray200)">
          {renderSubtitleText()}
        </Paragraph>
      </HeaderBox>

      {view === VIEW.CREATE && (
        <Create isHandy={isHandy} onComplete={onComplete} goBack={() => setView(VIEW.SELECT)} />
      )}

      {view === VIEW.CLAIM && (
        <Claim
          isHandy={isHandy}
          onComplete={onComplete}
          goBack={hasNoDevices ? null : () => setView(VIEW.SELECT)}
        />
      )}

      {view === VIEW.SELECT && (
        <Box fluid display="flex" gap="xs" marginTop={isHandy ? 'small' : 'medium'}>
          <BlueButton fluid size={isHandy ? 'small' : 'medium'} {...createNewButton}>
            {i18n('ClaimDevice.Select.CreateNew')}
          </BlueButton>
          <Button fluid size={isHandy ? 'small' : 'medium'} {...selectExistingButton}>
            {i18n('ClaimDevice.Select.SelectExisting')}
          </Button>
        </Box>
      )}
    </>
  );
};

export default ClaimDevice;
